import {
    EnsurePetAlertsRequest, EstimateModel, EstimateSalesItemModel, IBlobSasModel, IEstimateModel,
    IEstimateSalesItemModel, IPetAttachmentModel, IPetModel, IPetVisitRemindersModel, IPhotoModel,
    PetAttachmentModel, PetModel, PetVisitReminderModel, PhotoModel, IClinicAddNewPetRequestExistingClient,
    IMedicalHistoryModel, MedicalHistoryModel
} from "@common/models";
import { HospitalPrescriptionListingModel, IHospitalPrescriptionListingModel } from "@common/models/HospitalPrescriptionListingModel";
import { SetEstimateSalesPackageRequest } from "@common/models/requests";
import { AddEstimateSalesItemRequest } from "@common/models/requests/AddEstimateSalesItemRequest";
import { EstimateRequest } from "@common/models/requests/EstimateRequest";
import { UpdateEstimateSalesItemRequest } from "@common/models/requests/UpdateEstimateSalesItemRequest";
import { http } from "@common/utils";
import { DateTime } from "luxon";
import { trackPromise } from "react-promise-tracker";
import { ServiceHelper } from "./serviceHelper";
import { ServiceMethodOptions } from "./types";
import { VisitService } from "./visitService";
import { IPetWeightHistoryModel, PetWeightHistoryModel } from "@common/models/PetWeightHistoryModel";
import { PetCustomReminderModel } from "@common/models/PetCustomReminders";
export class PetService {
    private static baseUrl: string = '/api/pet';

    public static tags = {
        fetchPet: "fetchPet",
        addPet: "addPet",
        updatePet: "updatePet",
        getVisitReminders: "getVisitReminders",
        getHospitalPrescriptions: "getHospitalPrescriptions",
        ensurepetalerts: "ensurepetalerts",
        estimateIO: "estimateIO",
        addPetAttachment: "addPetAttachment",
        updateAttachment: "updateAttachment",
        getPetAttachments: "getPetAttachments",
        deletePetAttachments: "deletePetAttachments",
        deleteEstimate: "deleteEstimate",
		addNewPetFromClinic: "addNewPetFromClinic",
        getMedicalHistory: "getMedicalHistory",
        getPetWeightHistory: "weighthistory",
        createPetcustomReminders: "createPetcustomReminders",
    }

    static fetchPet = (petId: number, options?: ServiceMethodOptions): Promise<PetModel> =>
        trackPromise(new Promise<PetModel>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}`)
                .get<IPetModel>()
                .then((pet) => {
                    const petModel = new PetModel(pet);
                    resolve(petModel)
                })
                .catch(() => {
                    ServiceHelper.showError("Failed to fetch pet info.", options);
                    reject();
                });
        }), this.tags.fetchPet);

    static addPet = (request: PetModel): Promise<PetModel> =>
        trackPromise(new Promise<PetModel>((resolve, reject) => {
            http(`${this.baseUrl}`)
                .post<IPetModel>(request)
                .then(json => resolve(new PetModel(json)))
                .catch(reject)
        }), this.tags.addPet);

    static updatePet = (request: PetModel): Promise<PetModel> =>
        trackPromise(new Promise<PetModel>((resolve, reject) => {
            http(`${this.baseUrl}/${request.petId}`)
                .post<IPetModel>(request)
                .then(json => resolve(new PetModel(json)))
                .catch(reject)
        }), this.tags.addPet);

    static getVisitReminders = (petId: number): Promise<PetVisitReminderModel[]> =>
        trackPromise(new Promise<PetVisitReminderModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/visitreminders/${petId}?includeFinalizedAndPaidVisits=true`)
                .get<IPetVisitRemindersModel[]>()
                .then((json) => {
                    const reminders = json.map((reminder) => new PetVisitReminderModel(reminder));
                    resolve(reminders)
                })
                .catch(reject)
        }), this.tags.getVisitReminders);

    static getHospitalPrescriptions = (petId: number): Promise<HospitalPrescriptionListingModel[]> =>
        trackPromise(new Promise<HospitalPrescriptionListingModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/hospitalprescriptions/${petId}`)
                .get<IHospitalPrescriptionListingModel[]>()
                .then((json) => {
                    const hospitalPrescriptions = json.map((r) => new HospitalPrescriptionListingModel(r));
                    resolve(hospitalPrescriptions)
                })
                .catch(reject)
        }), this.tags.getHospitalPrescriptions);

    static ensurepetalerts = (petId: number, request: EnsurePetAlertsRequest): Promise<void> =>
        trackPromise(new Promise<void>((resolve, reject) => {
            http(`/api/ensurepetalerts/${petId}`) // This endpoint doesn't follow the /pet pattern but exists on the PetController
                .post<void>(request)
                .then(resolve)
                .catch(reject)
        }), this.tags.ensurepetalerts);

    static updatePetPhoto = (petId: number, request: IBlobSasModel): Promise<PhotoModel> =>
        trackPromise(new Promise<PhotoModel>((resolve, reject) => {
            http(`${this.baseUrl}/photo/${petId}`)
                .post<IPhotoModel>(request)
                .then(json => resolve(new PhotoModel(json)))
                .catch(reject)
        }), this.tags.addPet);

    static fetchEstimates = (petId: number): Promise<EstimateModel[]> =>
        trackPromise(new Promise<EstimateModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/estimate`)
                .get<IEstimateModel[]>()
                .then(json => json.map(em => new EstimateModel(em)))
                .then(resolve)
                .catch(reject)
        }), this.tags.estimateIO);

    static createEstimate = (clinicId: number, petId: number, petName: string, timezone: string): Promise<EstimateModel> => {
        const req: EstimateRequest = { clinicId, petId, name: `${petName} estimate ${DateTime.now().setZone(timezone).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}`, rowVersion: "" }
        return trackPromise(new Promise<EstimateModel>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/estimate`)
                .post<IEstimateModel>(req)
                .then(json => new EstimateModel(json))
                .then(resolve)
                .catch(reject)
        }), this.tags.estimateIO);
    }

    static updateEstimate = (clinicId: number, estimate: EstimateModel): Promise<EstimateModel> => {
        const req: EstimateRequest = { clinicId, petId: estimate.petId, name: estimate.name, rowVersion: estimate.rowVersion }
        return trackPromise(new Promise<EstimateModel>((resolve, reject) => {
            http(`${this.baseUrl}/${estimate.petId}/estimate/${estimate.estimateId}`)
                .post<IEstimateModel>(req)
                .then(json => new EstimateModel(json))
                .then(resolve)
                .catch(reject)
        }), this.tags.estimateIO);
    }

    static addEstimateSalesItem = (petId: number, estimateId: number, visitId: number, salesItemOptionId: number): Promise<EstimateSalesItemModel> => {
        const req: AddEstimateSalesItemRequest = {
            estimateId,
            visitId,
            salesItemOptionId
        }
        return trackPromise(new Promise<EstimateSalesItemModel>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/estimate/${estimateId}/item`)
                .post<IEstimateSalesItemModel>(req)
                .then(json => new EstimateSalesItemModel(json))
                .then(resolve)
                .catch(reject)
        }), this.tags.estimateIO);
    }

    static updateEstimateSalesItem = (petId: number, estimateSalesItem: EstimateSalesItemModel): Promise<EstimateSalesItemModel> => {
        const req: UpdateEstimateSalesItemRequest = {
            estimateSalesItemId: estimateSalesItem.estimateSalesItemId,
            quantity: estimateSalesItem.quantity,
            estimateQuantityLow: estimateSalesItem.estimateQuantityLow,
            estimateQuantityHigh: estimateSalesItem.estimateQuantityHigh,
            salesItemUnitPrice: estimateSalesItem.salesItemUnitPrice,
        }
        return trackPromise(new Promise<EstimateSalesItemModel>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/estimate/${estimateSalesItem.estimateId}/item/${estimateSalesItem.estimateSalesItemId}`)
                .put<IEstimateSalesItemModel>(req)
                .then(json => new EstimateSalesItemModel(json))
                .then(resolve)
                .catch(reject)
        }), this.tags.estimateIO);
    }

    static deleteEstimateSalesItem = (petId: number, estimateId: number, salesItemId: number): Promise<void> => {
        return trackPromise(new Promise<void>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/estimate/${estimateId}/item/${salesItemId}`)
                .delete()
                .then(() => resolve())
                .catch(reject)
        }), this.tags.estimateIO);
    }

    static addEstimateSalesPackage = (petId: number, estimateId: number, visitId: number, salesPackageId: number): Promise<EstimateModel> => {
        const req: SetEstimateSalesPackageRequest = { visitId, salesPackageId };
        return trackPromise(
            http(`${this.baseUrl}/${petId}/estimate/${estimateId}/setsalespackage`)
                .post<IEstimateModel>(req)
                .then(json => new EstimateModel(json))
            , this.tags.estimateIO);
    }

    static deleteEstimateSalesPackage = (petId: number, estimateId: number): Promise<EstimateModel> => {
        const req: SetEstimateSalesPackageRequest = { visitId: undefined, salesPackageId: undefined };
        return trackPromise(
            http(`${this.baseUrl}/${petId}/estimate/${estimateId}/setsalespackage`)
                .post<IEstimateModel>(req)
                .then(json => new EstimateModel(json))
            , this.tags.estimateIO);
    }

    static addEstimateSalesPlan = (petId: number, estimateId: number, visitId: number, salesPlanId: number): Promise<EstimateModel> => {
        const req = { visitId, salesPlanId };
        return trackPromise(
            http(`${this.baseUrl}/${petId}/estimate/${estimateId}/addsalesplan`)
                .post<IEstimateModel>(req)
                .then(json => new EstimateModel(json))
            , this.tags.estimateIO);
    }

    static addEstimateToVisit = (estimate: EstimateModel, visitId: number): Promise<EstimateSalesItemModel[]> =>
        trackPromise(new Promise<EstimateSalesItemModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/${estimate.petId}/estimate/${estimate.estimateId}/addtovisit/${visitId}`)
                .post<IEstimateSalesItemModel[]>({})
                .then(json => json.map(e => new EstimateSalesItemModel(e)))
                .then(resolve)
                .catch(reject)
        }), VisitService.tags.updateVisit);

    static addAttachment = (petId: number, hospitalId: number, blobSasModel: IBlobSasModel, documentTypeId: number, notes: string | undefined): Promise<PetAttachmentModel> =>
        trackPromise(new Promise<PetAttachmentModel>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/attachments`)
                .post<IPetAttachmentModel>({ petId, hospitalId, blobSasModel, documentTypeId, notes })
                .then(json => new PetAttachmentModel(json))
                .then(resolve)
                .catch(reject)
        }), this.tags.addPetAttachment);

        static updateAttachment = (petId: number, hospitalId: number, notes: string,petAttachmentId:number): Promise<PetAttachmentModel> =>
            trackPromise(new Promise<PetAttachmentModel>((resolve, reject) => {
                http(`${this.baseUrl}/${petId}/attachments`)
                    .post<IPetAttachmentModel>({ petId, hospitalId, petAttachmentId, notes })
                    .then(json => new PetAttachmentModel(json))
                    .then(resolve)
                    .catch(reject)
            }), this.tags.updateAttachment);

    static getAttachments = (petId: number): Promise<PetAttachmentModel[]> =>
        trackPromise(new Promise<PetAttachmentModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/attachments`)
                .get<IPetAttachmentModel[]>()
                .then(json => json.map(pa => new PetAttachmentModel(pa)))
                .then(resolve)
                .catch(reject)
        }), this.tags.getPetAttachments);

    static deleteAttachment = (petId: number, petAttachmentId: number): Promise<void> =>
        trackPromise(new Promise<void>((resolve, reject) => {
            http(`${this.baseUrl}/${petId}/attachments/${petAttachmentId}`)
                .delete()
                .then(() => resolve())
                .catch(reject)
        }), this.tags.deletePetAttachments);

    static deleteEstimate = (estimateId: number): Promise<void> =>
        trackPromise(new Promise<void>((resolve, reject) => {
            http(`${this.baseUrl}/estimate/delete/${estimateId}`)
                .delete()
                .then(() => resolve())
                .catch(reject)
        }), this.tags.deleteEstimate);

	static addNewPetFromClinic = (pet: IClinicAddNewPetRequestExistingClient, options?: ServiceMethodOptions): Promise<PetModel> =>
		trackPromise(
			new Promise<PetModel>((resolve, reject) => {
				http(`${this.baseUrl}/addfromclinic`)
					.post<IPetModel>(pet)
					.then((data) => resolve(new PetModel(data)))
					.then(() => ServiceHelper.showSuccess("Pet Added!", options))
					.catch((err) => {
						ServiceHelper.showError("Failed to Add Pet.", options);
						reject(err);
					});
			}), ServiceHelper.getTagForOptions(this.tags.addNewPetFromClinic, options));

    static getMedicalHistory = (petId: number): Promise<MedicalHistoryModel[]> =>
        trackPromise(new Promise<MedicalHistoryModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/medicalhistory/${petId}`)
                .get<IMedicalHistoryModel[]>()
                .then((json) => {
                    const history = json.map((medicalHistory: IMedicalHistoryModel) => new MedicalHistoryModel(medicalHistory))
                    resolve(history)
                })
                .catch(reject)
        }), this.tags.getMedicalHistory)

    static getPetWeightHistory = (petId: number): Promise<IPetWeightHistoryModel[]> =>
            trackPromise(new Promise<PetWeightHistoryModel[]>((resolve, reject) => {
                http(`${this.baseUrl}/weighthistory/${petId}`)
                    .get<IPetWeightHistoryModel[]>()
                    .then((petWeightHistoryJson) => {
                      const sortedPetWeightHistory = petWeightHistoryJson.sort((a, b) =>
                          new Date(b.visitDate).getTime() -
                          new Date(a.visitDate).getTime()
                      );
                      const sortedHistory = sortedPetWeightHistory.map(
                        (petWeightHistoryModel: IPetWeightHistoryModel) =>
                          new PetWeightHistoryModel(petWeightHistoryModel)
                      );
                      resolve(sortedHistory);
                    })
                    .catch(reject)
            }), this.tags.getPetWeightHistory)
    
    static savePetCustomReminders = ( reminders: PetCustomReminderModel[]): Promise<PetCustomReminderModel[]> => 
        trackPromise(new Promise<PetCustomReminderModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/customreminders`)
                .post<PetCustomReminderModel[]>(reminders)
                .then(json => json.map(pcr => new PetCustomReminderModel(pcr)))
                .then(resolve)
                .catch(reject)
        }), this.tags.createPetcustomReminders);
}
